import * as Modal from '#/components/shared/Modal';
import Button from '#/components/shared/Button';

import { useUsernamePromptModalActions } from '#/features/account/username-prompt-modal-context';
import { useDismissUsernamePrompt } from '#/features/account/useDismissUsernamePrompt';
import { useUsernameFormView } from '#/features/account/username-form-context';

import { Result } from '#/utils/types';

import * as UsernameForm from './UsernameForm';

export default function UsernamePromptModal() {
  const modalActions = useUsernamePromptModalActions();
  const formView = useUsernameFormView();
  const dismissUsernamePrompt = useDismissUsernamePrompt();

  const isDismissable = !formView.isSubmitting;
  const isDisabled = formView.isSubmitting;

  /**
   * Closes the modal and dismisses the username
   * prompt so that the user is not prompted again.
   */
  const handleDismiss = () => {
    if (!isDismissable) return;
    dismissUsernamePrompt();
    modalActions.close();
  };

  /**
   * Closes the modal without changing the account
   * so that the user can be prompted again later.
   */
  const handleRemindLater = () => {
    modalActions.close();
  };

  const handleSubmitComplete = (result: Result<void>) => {
    if (!result.ok) return;
    modalActions.close();
  };

  return (
    <Modal.Root
      isOpen
      onDismiss={handleDismiss}
      aria-labelledby="username-prompter-modal"
    >
      <Modal.Header>
        <Modal.Title id="username-prompter-modal">
          Create Your Username
        </Modal.Title>
        {isDismissable && <Modal.CloseButton onClick={handleDismiss} />}
      </Modal.Header>

      <Modal.Paragraph>
        Your username will be visible to other users unless you choose to hide
        it. You can always change it later.
      </Modal.Paragraph>

      <UsernameForm.Form onSubmitComplete={handleSubmitComplete}>
        <UsernameForm.Fields />
        <UsernameForm.Actions>
          <Button
            type="button"
            onClick={handleRemindLater}
            disabled={isDisabled}
            variant="outline"
          >
            Remind Later
          </Button>
          <UsernameForm.Submit />
        </UsernameForm.Actions>
      </UsernameForm.Form>
    </Modal.Root>
  );
}
